<script setup>
import {onMounted, ref, watch} from 'vue';
import {codePreview, formatDate, maskPhoneNumber, setButtonContentDefault, setButtonContentLoading} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import documentService from '@/services/DocumentService';
import {authData, generate2faCode, getLoggedUser, getToken} from '@/services/AuthService';
import {jwtDecode} from "jwt-decode";
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const props = defineProps({
  documentsIds: {type: Array, required: true},
});
const emit = defineEmits(['confirm', 'cancel']);

const phone = authData.loggedUser.phone;
const errorMessage = ref('');
const twoFactorCode = ref(null);
const codeSent = ref(false);
const codeSentAgain = ref(false);
const codeRequestInterval = ref(0);
const codeRequestProcessing = ref(false);
const isOpen = ref(true)
const showRejectDocumentsInfo = ref(false);
const rejectDocumentsDescription = ref('');
const signDocumentsStep = ref('INIT');      // INIT, REJECT, CONFIRM
const buttonRef = ref(null);

const closeModalWith = (value) => {
  isOpen.value = false;
  emit(value);
}

function openModal() {
  isOpen.value = true
}

function impersonator() {
  // getToken() vrací token aktivní pro tento panel - u impersonace jde o token
  // ze sessionStorage (localStorage nese pořád jen původní, neimpersonovanou identitu).
  const token = jwtDecode(getToken());
  if (!token || token.impersonator === null) {
    return false
  }
  return token.impersonator;
}

function bulkSign() {
  if (!twoFactorCode.value) {
    errorMessage.value = t('signModal.fill_the_auth_code');
    return;
  }
  setButtonContentLoading('signButton');
  documentService.bulkSignDocuments(props.documentsIds, twoFactorCode.value)
    .then(() => {
      setButtonContentDefault('signButton');
      closeModalWith('confirm');
    })
    .catch((error) => {
      setButtonContentDefault('signButton');
      errorMessage.value = error.response.data.message;
      if (error.response.data.message === 'verification_code.INVALID_CODE') {
        errorMessage.value = t('signModal.invalid_auth_code');
      }
      if (error.response.data.message === 'verification_code.TOO_MANY_ATTEMPTS') {
        errorMessage.value = t('verification_code.TOO_MANY_ATTEMPTS');
      }
    });
}

function rejectDocuments() {
  documentService.rejectDocuments(props.documentsIds, rejectDocumentsDescription.value)
    .then(() => {
      closeModalWith('confirm');
    })
    .catch((error) => {
      errorMessage.value = error.response?.data?.message ?? errorMessage.value;
    });
}

const generateCode = () => {
  console.log(authData);
  codeSentAgain.value = true;
  errorMessage.value = null;
  const form = {
    verifiable_type: 'DOCUMENT',
    verifiable_id: props.documentsIds[0],
    service: 'SMS',
    recipient: authData.loggedUser.phone,
  }
  setButtonContentLoading('btn-activate');
  codeRequestProcessing.value = true;
  generate2faCode(form).then(response => {
    codeRequestInterval.value = 30;
    codeSent.value = true;
    codeRequestProcessing.value = false;
    setButtonContentDefault('btn-activate');
    startStopwatch();
  },(error) => {
    errorMessage.value = error.message;
    setButtonContentDefault('btn-activate');
    codeSentAgain.value = false;
    codeRequestProcessing.value = false;
  });
}

const startStopwatch = () => {
  const interval = setInterval(() => {
    codeRequestInterval.value--;
    if (codeRequestInterval.value <= 0) {
      clearInterval(interval);
      codeSentAgain.value = false;
    }
  }, 1000);
}

function revertRejectDocuments() {
  showRejectDocumentsInfo.value = false;
  rejectDocumentsDescription.value = '';
}

function setStep(step) {
  if(step === 'CONFIRM') {
    generateCode();
  }
  signDocumentsStep.value = step;
}
</script>

<template>
  <TransitionRoot :show="isOpen" as="template">
    <Dialog :initialFocus="buttonRef" as="div"  class="modal show d-block" style="z-index: 1055;" >
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('signModal.sign_document') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>

            <div v-if="signDocumentsStep === 'INIT'">
              <div class="modal-body text-center">
                <button v-if="!impersonator()" ref="buttonRef" class="btn btn-primary btn-md" type="button" @click="setStep('CONFIRM')">{{ t('signModal.sign') }}</button>
                <span v-if="impersonator()">{{ t('signModal.cannot_sign_this_document') }}</span>
              </div>
              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('signModal.cancel') }}</button>
                <button v-if="!impersonator()" class="btn btn-danger btn-md" type="button" @click="setStep('REJECT')">{{ t('signModal.return_documents') }}</button>
              </div>
            </div>
            <div v-if="signDocumentsStep === 'REJECT'">
              <div class="modal-body">
                <label class="form-label">{{ t('signModal.refusal_reason2') }}</label>
                <textarea v-model="rejectDocumentsDescription" class="form-control" rows="3"></textarea>
              </div>
              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="setStep('INIT')">{{ t('signModal.back') }}</button>
                <button class="btn btn-danger btn-md" type="button" @click="rejectDocuments">{{ t('signModal.confirm_return_documents') }}</button>
              </div>
            </div>
            <div v-if="signDocumentsStep === 'CONFIRM'">

              <div class="modal-body">
                <div class="form-group">
                  <div class="text-center mb-3">{{ t('signModal.enter_auth_code') }}</div>
                  <label class="form-label" for="2fa_code">{{ t('signModal.phone_number2') }}</label>
                  <input id="2fa_code" class="form-control"
                         type="text" placeholder="" autocomplete="off" readonly disabled
                         :value="maskPhoneNumber(phone)" required/>
                </div>
                <div class="form-group">
                  <div class="form-label d-flex justify-content-between">
                    <label for="2fa_code">{{ t('signModal.auth_code') }}</label>
                  </div>
                  <input id="2fa_code" class="form-control"
                         type="text" placeholder="" autocomplete="off"
                         v-model="twoFactorCode" required/>
                  <div class="mt-2">
                    <span v-if="codeRequestProcessing" class="text-muted">{{ t('signModal.sending_auth_code') }}</span>
                    <a :class="codeSentAgain === true ? 'pointer-events-none' : ''" class="cursor-pointer" @click="generateCode" v-if="!codeRequestProcessing && codeRequestInterval === 0"><span>{{ t('signModal.send_code') }} </span> <span v-if="codeSent"> {{ t('signModal.again') }} </span> </a>
                    <span class="text-muted" v-if="!codeRequestProcessing && codeRequestInterval > 0">{{ t('signModal.auth_code_sent') }} {{codeRequestInterval}}{{ t('signModal.secs') }}</span>
                  </div>
                </div>
                <div class="alert alert-danger mt-3" v-if="errorMessage"> {{errorMessage}} </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="setStep('INIT')">{{ t('signModal.back') }}</button>
                <button id="signButton" class="btn btn-primary w-25" type="button" @click="bulkSign"><span>{{ t('signModal.sign') }}</span></button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

